// `spaces` PUT dispatch for createSpace.
//
// The adapter generates a UUID v4, INSERTs the optimistic space row
// locally with a placeholder serial_number + displayName, then this
// connector POSTs the same id to /capture/sessions/:sessionId/spaces.
//
// The session id isn't carried on the spaces table itself — capture
// sessions reference a building, and a space references the same
// building, but the session-to-space link is implicit through the
// building. The connector recovers the session id by reading the
// most-recent capture_sessions row for the matching building_id —
// preferring an open session, falling back to a completed one so an
// offline room create still lands after the session completed (#664).
// In practice there's exactly one such row per (user, building) at a
// time per the operational policy "one surveyor per building" (PRD).
//
// RESPONSE DISPOSITION. 5xx / network / any UNCLASSIFIED 4xx → throw (entry
// stays queued, the SDK retries). not_found (404, incl. a code-less proxy 404)
// → rethrown to the central backstop (connector-upload-isolation.ts), which
// drains it for spaces (a cancelled / admin-deleted owning session, or the
// C6/#692 off-catalogue-floor cascade). Two responses are handled INLINE here
// instead, so a never-retryable one can't WEDGE the whole device queue
// (uploadData acks the batch only once EVERY entry settles, so one stuck entry
// blocks every later mutation — including contractual counts — while the UI
// shows the room saved): invalid_argument is DRAINED; already_exists is
// RE-MINTED or RETRIED (it is NOT a drain — see below):
//
//   invalid_argument (#785) — the server RE-VALIDATED the create and rejected
//   it permanently: the room's room-type was deleted / removed from the active
//   contract / is pseudo / not serialisable (RoomTypeNotAcceptableError), or the
//   session is site-scoped (SessionNotBuildingScopeError) — see
//   create-space-error-mapping.ts. It can never land, so delete the optimistic
//   local row + emit dropped(superseded) + ack. Any counts the surveyor captured
//   under the room preserve themselves via their OWN create-item not_found
//   quarantine ("held safely"); the room itself is recoverable reference data
//   (PRD §6.5), so superseded is suppressed from DroppedChangeToast. This follows
//   the asset-types H20 drain-not-rethrow PATTERN, but is CODE-scoped (err.code
//   === 'invalid_argument') where H20 is STATUS-scoped (any permanent 4xx). The
//   split is deliberate: code-scoping keeps not_found on the central backstop
//   (C6's "no inline 404 carve-out for spaces") and lets a coded permission_denied
//   rethrow. The create endpoint emits a CLOSED set of Encore codes, so an UNCODED
//   4xx can only come from infra (proxy/WAF) — it is left to the conservative
//   rethrow-and-retry default (an uncoded 400 is ambiguous; "rather over-retry
//   than ack-and-drop a write that could still land"), and the uncoded-404 case is
//   already drained by the backstop's status fallback. Scoped to the CREATE path
//   (NOT the whole spaces table) ON PURPOSE: a spaces ALIAS PATCH can also 400
//   invalid_argument (connector-dispatch-space-update.ts re-validates the alias
//   and rethrows it), and a table-wide drain would make isolatePoisonEntry DELETE
//   the room over a bad alias edit. permission_denied likewise stays a rethrow so
//   a genuine ownership failure surfaces, never silently drops a surveyor's room.
//
//   already_exists — splits the two flavours of 409 the backend can return:
//
//   already_exists WITH details.existingSpaceId — the client UUID
//   collided with an UNRELATED space at a different (building, floor,
//   room type) scope (create-space-client-id.ts → assertNoSpaceScope
//   IdConflict). The backend only raises this flavour when the existing
//   row is at a DIFFERENT scope — a same-scope re-POST of our own id
//   takes the idempotent-success path — so THIS room is genuinely not on
//   the server yet and still needs creating. Deleting it (the previous
//   behaviour) was silent contractual data-loss. Instead we RE-MINT it:
//   copy the row to a fresh client UUID and re-INSERT, so it re-enters
//   the upload queue under a collision-free id and uploads cleanly next
//   cycle. The surveyor's room is preserved the whole time — it never
//   leaves the spaces list, only its id changes. The old row's local
//   DELETE acks as a no-op in the connector's spaces DELETE branch (the
//   server never accepted the colliding id). A random-UUID collision is
//   should-never-happen, so we keep a Sentry breadcrumb for telemetry
//   but emit NO `dropped` event — nothing is lost, so the surveyor is
//   not told to re-add anything.
//
//   already_exists WITHOUT details.existingSpaceId — the
//   (room type, serial) unique-violation race (create-space.ts:464).
//   The backend message literally says "retry", and the next attempt
//   recomputes a fresh server-assigned serial and succeeds, so this
//   still THROWS (entry stays queued). Dropping it would lose a
//   legitimately-new room.

import type { CrudEntry } from '@powersync/web'
import type { PowerSyncWriteDb } from './powersync-db'
import * as Sentry from '@sentry/react'

import { ApiError, apiFetch } from '../api'

import type { DispatchContext } from './connector-dispatch-context'
import { getSessionRehome } from './connector-session-rehome-registry'
import { _emitDropped, _emitUploaded } from './upload-events'

interface SpaceUploadPostBody {
  id: string
  floorId: string
  roomTypeId: string
  alias?: string | null
}

export async function dispatchSpaceInsert(
  entry: CrudEntry,
  db: PowerSyncWriteDb,
  context: DispatchContext,
): Promise<void> {
  const data = entry.opData ?? {}

  const buildingId = data['building_id']
  if (typeof buildingId !== 'string' || buildingId.length === 0) {
    throw new Error(
      `dispatchSpaceInsert: spaces row ${entry.id} is missing building_id; ` +
        `the local INSERT must populate it from the active capture session.`,
    )
  }

  // #664 + Codex #669 review #2: resolve the session this room belongs to.
  //
  // A room created offline carries created_in_session_id (its origin session).
  // Resolve THAT session: its 409 re-home if the origin orphan was re-homed
  // (getSessionRehome — active OR completed post-#664), else the origin row
  // itself if it still exists locally (open OR completed; #664 lets a late
  // create land on a completed session). We deliberately do NOT fall back to the
  // building's "latest session" for a room that knows its origin — if the origin
  // was CANCELLED (hard-deleted) a building-wide lookup could route the room to
  // an unrelated prior completed session and REVIVE cancelled work server-side
  // (Codex #2). Origin gone and not re-homed ⟹ cancel intent wins: drop the room
  // (visible via `dropped`/superseded; never wedge).
  const createdInSessionId =
    typeof data['created_in_session_id'] === 'string' &&
    data['created_in_session_id'].length > 0
      ? data['created_in_session_id']
      : null

  let sessionId: string | null
  if (createdInSessionId !== null) {
    const rehomedOrigin =
      getSessionRehome(createdInSessionId)?.authoritativeId ?? null
    if (rehomedOrigin !== null) {
      sessionId = rehomedOrigin
    } else {
      const rows = await db.getAll<{ id: string }>(
        `SELECT id FROM capture_sessions WHERE id = ? LIMIT 1`,
        [createdInSessionId],
      )
      sessionId = rows[0]?.id ?? null
    }
    if (sessionId === null) {
      // Origin session gone (cancelled, hard-deleted) and not re-homed. Reviving
      // the room on an unrelated building session would resurrect cancelled work
      // (Codex #2), so drop it — cancel intent wins. `superseded` is suppressed
      // from the toast because the surveyor deliberately discarded this session.
      //
      // Remove the optimistic local spaces row too, so a cancelled room doesn't
      // linger as a phantom in the UI after a building reopen (Codex review of
      // #669: "Delete canceled-origin rooms before acking"). The resulting spaces
      // DELETE carries no pending_space_deletes tombstone, so routeSpaceDelete
      // acks it locally — no server POST. Idempotent: a no-op if already gone.
      await db.execute(`DELETE FROM spaces WHERE id = ?`, [entry.id])
      Sentry.captureMessage(
        '[powersync] spaces create dropped — origin session cancelled (no revive)',
        {
          level: 'warning',
          extra: {
            reason: 'powersync_space_create_origin_cancelled_drop',
            entryId: entry.id,
            createdInSessionId,
            buildingId,
          },
        },
      )
      console.warn(
        '[powersync] spaces create dropped — origin session gone (cancelled); ' +
          'not reviving on an unrelated session',
        { id: entry.id, createdInSessionId, buildingId },
      )
      _emitDropped(entry.table, entry.id, 'superseded')
      return
    }
  } else {
    // Legacy rows without an origin stamp: fall back to the building's session
    // (#664, includeCompleted=true). A genuinely orphaned create (no session at
    // all) stays a loud programmer-error throw, not a silent wedge.
    sessionId = await loadSessionIdForBuilding(db, buildingId, context, true)
    if (sessionId === null) {
      throw new Error(
        `dispatchSpaceInsert: no capture_sessions row (open or completed) found ` +
          `for building ${buildingId}; the user must have a session before the ` +
          `spaces PUT reaches the upload queue.`,
      )
    }
  }

  const body: SpaceUploadPostBody = {
    id: entry.id,
    floorId: String(data['floor_id'] ?? ''),
    roomTypeId: String(data['room_type_id'] ?? ''),
  }
  // Preserve a surveyor-entered alias on the OFFLINE create path too. An
  // INSERT's opData carries every column, so alias is present here. Without
  // this, an offline-created room's custom name is lost (the server creates
  // it with alias=null and the next sync overwrites the local value); the
  // online transport path already forwards it (Codex P2). Omit when blank so
  // the server applies its own null default; the backend trims/normalises.
  const aliasValue = data['alias']
  if (typeof aliasValue === 'string' && aliasValue.length > 0) {
    body.alias = aliasValue
  }

  try {
    await apiFetch(
      `/capture/sessions/${encodeURIComponent(sessionId)}/spaces`,
      { method: 'POST', json: body },
    )
    _emitUploaded(entry.table, entry.id)
    return
  } catch (err) {
    if (err instanceof ApiError && err.code === 'already_exists') {
      const existingSpaceId = readExistingSpaceId(err)
      if (existingSpaceId !== undefined) {
        // UUID collision at a DIFFERENT scope — see file header. The
        // colliding server row is a different room, so THIS room is not
        // on the server and must still be created. Deleting it would be
        // silent contractual data-loss, so re-mint it under a fresh id:
        // the room is preserved (it stays in the spaces list) and
        // re-uploads cleanly next cycle. Keep a Sentry breadcrumb because
        // a random-UUID collision is should-never-happen; emit NO
        // `dropped` event because nothing is lost.
        const remintedId = crypto.randomUUID()
        Sentry.captureException(err, {
          level: 'warning',
          extra: {
            reason: 'powersync_space_id_collision_reminted',
            entryId: entry.id,
            existingSpaceId,
            remintedId,
            payload: data,
          },
        })
        console.warn(
          '[powersync] spaces already_exists (id collision) — re-minting ' +
            'under a fresh id so the room is preserved',
          { id: entry.id, existingSpaceId, remintedId },
        )
        await remintSpaceUnderNewId(db, entry.id, remintedId)
        return
      }
      // No existingSpaceId → (room type, serial) unique-violation race.
      // Backend says "retry"; fall through to rethrow so the entry stays
      // queued and the next attempt picks a fresh serial.
    }
    // invalid_argument (#785): the server re-validated the room create and
    // rejected it PERMANENTLY — the room-type was deleted / de-contracted / is
    // pseudo / not serialisable (RoomTypeNotAcceptableError), or the session is
    // site-scoped (SessionNotBuildingScopeError); see create-space-error-
    // mapping.ts. It can NEVER succeed on retry with the same payload, and a room
    // holds no count of its own, so a bare rethrow would WEDGE the whole device
    // upload queue behind this one entry (uploadData acks the batch only once
    // EVERY entry settles) while the UI shows the room saved. Drain it inline —
    // mirroring the asset-types H20 drain and the cancelled-origin drop above:
    // delete the optimistic local spaces row (no authoritative row will ever sync
    // down to evict it), emit dropped(superseded) so the held-count probe
    // re-reads, then return so batch.complete() acks. Counts the surveyor captured
    // under the room reference a space the server never created, so each one's own
    // create-item POST gets not_found and is preserved in the device quarantine
    // ("held safely") — no count is silently dropped. superseded is suppressed
    // from DroppedChangeToast (a room is recoverable reference data, not a count).
    // We match err.code === 'invalid_argument' specifically (NOT the table-aware
    // permanent set) so a spaces ALIAS PATCH's invalid_argument cannot reach the
    // central backstop and wrongly DELETE the room; not_found stays the backstop
    // drain (create-space C6 "no inline 404 carve-out for spaces").
    if (err instanceof ApiError && err.code === 'invalid_argument') {
      Sentry.captureException(err, {
        level: 'warning',
        extra: {
          reason: 'powersync_space_create_rejected',
          entryId: entry.id,
          buildingId,
          status: err.status,
          code: err.code,
          drained: true,
        },
      })
      console.warn(
        '[powersync] spaces create rejected by the server (permanent ' +
          'invalid_argument — room type removed/de-contracted) — drained to keep ' +
          'the upload queue moving; any counts captured under it are preserved in ' +
          'the device quarantine',
        { id: entry.id, buildingId, status: err.status },
      )
      await db.execute(`DELETE FROM spaces WHERE id = ?`, [entry.id])
      _emitDropped(entry.table, entry.id, 'superseded')
      return
    }
    throw err
  }
}

/**
 * Re-mint an un-syncable optimistic space under a fresh id. Copies every
 * column of the existing local row to a new row keyed by `newId`, then
 * deletes the old row. The new row re-enters the PowerSync upload queue as
 * its own INSERT (uploaded next cycle under the collision-free id); the
 * old row's DELETE acks as a local-only no-op in the connector's spaces
 * DELETE branch — the server never accepted the colliding id, so there is
 * nothing to delete remotely. The room is never removed from the
 * surveyor's view; only its id changes.
 *
 * capture_items already counted against the OLD id are deliberately NOT
 * re-pointed here. `space_id` is not a client-writable column on the
 * updateItem write path, so a local `UPDATE capture_items SET space_id`
 * produces a column-only PATCH that the connector turns into an invalid
 * updateItem PUT (no quantity/syncVersion) and rejects — wedging the
 * whole upload queue (adversarial review). Those items' own INSERTs also
 * carry the old, collided space id, so they cannot land server-side
 * regardless: they surface through the create-item not_found drop +
 * DroppedChangeToast safety net (the loss is reported, never silent, and
 * no worse than the previous delete-the-room behaviour). The collision is
 * an essentially-impossible random-UUID clash; full preservation of any
 * in-room counts (re-INSERT under the new space, or a local-only
 * quarantine) is a follow-up, not worth a queue-wedge risk here.
 */
async function remintSpaceUnderNewId(
  db: PowerSyncWriteDb,
  oldId: string,
  newId: string,
): Promise<void> {
  await db.execute(
    `INSERT INTO spaces (
       id, building_id, floor_id, room_type_id, serial_number,
       display_name, alias, is_pseudo, is_field_added, requires_review,
       created_at, updated_at, created_in_session_id
     )
     SELECT ?, building_id, floor_id, room_type_id, serial_number,
            display_name, alias, is_pseudo, is_field_added, requires_review,
            created_at, updated_at, created_in_session_id
     FROM spaces WHERE id = ?`,
    [newId, oldId],
  )
  await db.execute(`DELETE FROM spaces WHERE id = ?`, [oldId])
}

/**
 * Read the `existingSpaceId` the backend attaches (via withDetails) to
 * the scope/UUID-collision flavour of already_exists. Returns undefined
 * for the (room type, serial) unique-violation flavour, which carries no
 * details and must be retried rather than dropped. `ApiError` preserves
 * the raw `{ code, message, details }` body on `err.body`.
 */
function readExistingSpaceId(err: ApiError): string | undefined {
  const body = err.body
  if (typeof body !== 'object' || body === null) return undefined
  const details = (body as { details?: unknown }).details
  if (typeof details !== 'object' || details === null) return undefined
  const id = (details as { existingSpaceId?: unknown }).existingSpaceId
  return typeof id === 'string' && id.length > 0 ? id : undefined
}

/**
 * Find the capture session for the given building. The connector needs
 * this to construct the POST/PUT URL since spaces (and floors) don't
 * carry a session_id column. PowerSync's read sync stream keeps
 * capture_sessions populated for the active user, and the operational
 * policy "one surveyor per building at a time" ensures at most one OPEN
 * row matches.
 *
 * Exported for reuse by dispatchFloorInsert — the floors table likewise
 * carries no session id, so the offline New Room floor PUT recovers the
 * session the same way.
 *
 * `includeCompleted` (default false) preserves the strict open-only
 * lookup the create / floor / item-recovery callers rely on. The
 * alias-edit dispatch (connector-dispatch-space-update.ts) passes `true`:
 * a room rename queued OFFLINE must still resolve a session if the
 * building's session was completed during the offline window, otherwise
 * the queued alias edit is silently dropped (#667). It falls back to the
 * building's most-recent completed session only when none is open. A
 * rename touches only the cosmetic alias — no captured counts, no new
 * rows, no building-status flip — so authorising it against a completed
 * session is safe (the alias endpoint enforces the same).
 */
export async function loadSessionIdForBuilding(
  db: PowerSyncWriteDb,
  buildingId: string,
  context?: DispatchContext,
  includeCompleted = false,
): Promise<string | null> {
  // Prefer an in-batch session re-home. When this building's optimistic
  // capture_sessions row was re-homed onto the authoritative ACTIVE
  // session earlier in the same batch (connector-dispatch-session-
  // insert.ts), the orphan local row has already been deleted, so the
  // SELECT below would miss it and race the read-sync stream that
  // delivers the authoritative row. The rewrite carries the
  // authoritative id directly, so the dependent spaces/floors PUTs
  // target the live session instead of wedging. See
  // connector-dispatch-context.ts (sessionRehomeByBuilding).
  const rehomed = context?.sessionRehomeByBuilding.get(buildingId)
  if (rehomed !== undefined) return rehomed

  // includeCompleted: `(completed_at IS NULL) DESC` sorts any open session
  // first, then most-recent started — so an open session always wins,
  // falling back to the building's latest completed session only when none
  // is open. The default path stays strictly open-only.
  const rows = includeCompleted
    ? await db.getAll<{ id: string }>(
        `SELECT id FROM capture_sessions
         WHERE building_id = ?
         ORDER BY (completed_at IS NULL) DESC, started_at DESC
         LIMIT 1`,
        [buildingId],
      )
    : await db.getAll<{ id: string }>(
        `SELECT id FROM capture_sessions
         WHERE building_id = ? AND completed_at IS NULL
         ORDER BY started_at DESC
         LIMIT 1`,
        [buildingId],
      )
  return rows[0]?.id ?? null
}
